import type { WorkHandoff } from '@shared/types/thread-work';
import type { HandoffsView } from '@/lib/work-handoffs';
import type { MemberFile } from '@/lib/team-scope';
import { projectActivityLivingWorks } from './thread-living-model';
import { buildTodayRows } from './today-pipeline';
import { groupTodayEntries, type TodayEntry } from './today-launches';
import { daysSince, planTodayRows, sortEngagements, type PlannedTodayRow } from './today-horizons';

/** The rows of « En cours » for planned work: engagements first, then suggestions, one launch per task. */
export const currentEntries = (items: PlannedTodayRow[]): TodayEntry[] => groupTodayEntries([
  ...sortEngagements(items.filter((item) => item.commitment === 'engagement')),
  ...items.filter((item) => item.commitment === 'suggestion'),
]);

export interface FileSummary {
  entries: TodayEntry[];
  incoming: WorkHandoff[];
  /** Rows of their file: relays received, then tasks (rituals aside). */
  tasks: number;
  /** Days since the oldest task was taken on or opened. */
  oldestDays: number | null;
  /** Objects waiting in their launches. */
  objects: number;
}

/**
 * A member's file as they see it (team scope, 21/10/2026): the same model as
 * their own Mon travail, so the manager's figures are theirs.
 */
export const summarizeFile = (file: MemberFile, handoffs: HandoffsView, now: Date): FileSummary => {
  const planned = planTodayRows(buildTodayRows(projectActivityLivingWorks(file.subjects), file.apps), now)
    .filter((item) => item.bucket === 'today' && !handoffs.passedTopicIds.has(item.row.topic.id));
  const entries = currentEntries(planned);
  const ages = planned.map((item) => daysSince(item.row.topic.committedAt ?? item.row.topic.openedAt, now));
  return {
    entries,
    incoming: handoffs.incoming,
    tasks: entries.length + handoffs.incoming.length,
    oldestDays: ages.length > 0 ? Math.max(...ages) : null,
    objects: entries.reduce((total, entry) => total + (entry.kind === 'launch' ? entry.launch.recordIds.length : 0), 0),
  };
};
